import {Button, Checkbox, Col, Form, Input, Modal, Radio, RadioChangeEvent, Row, Select, Space, Spin} from 'antd';
import {CheckboxValueType} from 'antd/lib/checkbox/Group';
import _ from 'lodash';
import React, {useContext, useEffect, useState} from 'react';
import {apiFetchMapping} from '../../../../containers/QueryBuilder/queryBuilderApi';
import {CodeValue} from '../../../../containers/QueryBuilder/types/CodeValue';
import {useAppSelector} from '../../../../hooks';
import ProceduresSelector from '../../../create/ProceduresSelector';
import {QueryBuilderContext, QueryBuilderContextType} from '../../QueryBuilderContext';
import DiagnosisFilter from './DiagnosisFilter';
import MinMaxFilter from './MinMaxFilter';
import PeriodFilter from './PeriodFilter';
import WaitingTimeFilter from './WaitingTimeFilter';
import GenericTreeFilter from "./GenericTreeFilter";
import ClinicalEventFilter from "./ClinicalEventFilter";
import RemoteSelect, {fetchOptionsList} from "./RemoteSelect";
import SelectWrapper from '../../../create/SelectWrapper';
import {AllButtonStatusTypes, FilterDialogContext} from "./FilterDialogContext";
import {MAX_AGE, MIN_AGE} from "../../../create/AgeSelector";
import LikeFilter from "./LikeFilter";
import {cohortMonthsField, cohortPeriodField, isCohortField} from "../../QueryBuilder";
import CohortsFilter, {cohortDescription} from "./CohortsFilter";
import {apiFetchOptions} from "../../../create/selectorApi";
import {LabelAndKey} from "../../../create/types/LabelAndKey";

const { Option } = Select

export const findLabel = (choices: CodeValue[] | undefined, value: any, fieldCode?: string) => {
    return fieldCode === 'clinicalevent_custom_opcrdc' || fieldCode === 'treatment_custom_opcrdt'
        ? choices?.find(j => j.code === value.toString())?.name
        : choices?.find(j => j.code === value.toString())?.opt;
}

export const findLabels = (choices: CodeValue[] | undefined, values: any[], fieldCode?: string) => {
    return values.map(i => findLabel(choices, i, fieldCode))
}

const FilterDialog: React.FC = () => {

    const {
        userQuery,
        setUserQuery,
        currentField,
        setCurrentField,
        isFilterModalOpen,
        setIsFilterModalOpen,
        onFilterFinish,
        setOnFilterFinish,
        cohortId,
    } = useContext(QueryBuilderContext) as QueryBuilderContextType

    const [selected, setSelected] = useState<any[] | undefined>(undefined)
    const [choices, setChoices] = useState<CodeValue[] | undefined>(undefined)
    const [okButton, setOkButton] = useState<boolean>(false)
    const [allButtonStatus, setAllButtonStatus] = useState<AllButtonStatusTypes>('show')
    const [cohortChoices, setCohortChoices] = useState<LabelAndKey[] | undefined>(undefined)

    const [form] = Form.useForm()

    const handleCancel = () => {
        setIsFilterModalOpen(false)
        setCurrentField(undefined)
    }

    useEffect(() => {
        if (currentField && currentField.field.dialogType !== 'select_remote') {
            apiFetchMapping(currentField.field.code)
                .then(i => setChoices(i))
        }
        if (allButtonStatus !== 'show') {
            setAllButtonStatus('show')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentField])

    useEffect(() => {
        setAllButtonStatus(choices && choices.length > 100 ? 'disable' : 'show')
    }, [choices])

    useEffect(() => {
        if (currentField && isCohortField(currentField.field.code)) {
            apiFetchOptions('/cohorts?cohortId=' + cohortId, setCohortChoices)
        } else {
            setCohortChoices([])
        }
    }, [cohortId, currentField])

    const handleChange = (value: any[]) => {
        if (value && value.length > 0) {
            setOkButton(true)
        } else {
            setOkButton(false)
        }
    }

    const handleSelectChange = (values: string[] | Array<CheckboxValueType>) => {
        const labels = findLabels(choices, values, currentField!.field.code)
        form.setFieldValue(`${currentField!.field.code}_labels`, labels)
        setSelected(values)
        handleChange(values)
    }

    const onFinish = (formData: any) => {
        console.log('onFinish - formData:', formData)

        const field = _.find(userQuery.query.filters, {code: currentField?.field.code})

        if (!field) {
            handleCancel()
            return
        }

        const data = formData[field.code]

        if(data === undefined) {
            handleCancel()
        }

        if (data !== undefined) {
            if (_.isArray(data)) {
                field.value = data
                    .map((i: any) => _.has(i, 'value') ? i.value : i)
                    .join(',')
            } else {
                // e.g. min/max age filter
                field.value = data
            }

            if (isCohortField(field.code)) {
                const PERIOD_FIELD = cohortPeriodField(field.code)
                const period = _.find(userQuery.query.filters, {'code': PERIOD_FIELD})!
                period.value = formData[PERIOD_FIELD]

                const MONTHS_FIELD = cohortMonthsField(field.code)
                const months = _.find(userQuery.query.filters, {'code': MONTHS_FIELD})!
                months.value = formData[MONTHS_FIELD]
            }

            _.remove(userQuery.query.filterLabels, {field: field.code})

            let labels = formData[`${field.code}_labels`]
            if (!labels) {
                labels = data
                    .map((i: any) => _.has(i, 'label') ? i.label : i)
            }

            userQuery
                .query
                .filterLabels
                .push({
                    field: field.code,
                    title: currentField!.field.name,
                    values: labels
                })

            if (onFilterFinish) {
                onFilterFinish(userQuery.query)
                setOnFilterFinish(undefined)
            }

            setUserQuery(userQuery)
            handleCancel()

        }
    }

    const onFinishFailed = (errorInfo: any) => {
        console.log('Failed:', errorInfo)
    }

    const onCheckAll = () => {
        const previousValue = form.getFieldValue(currentField!.field.code) || [];
        const totalChoices = [...previousValue, ...(choices?.map(i => i.code) || [])];
        const uniqueChoices = totalChoices.reduce((prev: { label: string, value: number }[], current:{ label: string, value: number }) => {
            const isPresent = prev.find(itemInArray => itemInArray.value === current.value) !== undefined;
            if (isPresent) {
                return prev;
            }
            return [...prev, current];
        }, []);
        form.setFieldValue(currentField!.field.code, uniqueChoices)
        handleChange(uniqueChoices)
    }

    const [onSelectAll, setOnSelectAll] = useState<() => void>(() => { return onCheckAll })

    const procedureTree = useAppSelector((state) => state.settings.values.PROCEDURETREE)!
    const procedureIPDNTree = useAppSelector((state) => state.settings.values.PROCEDUREIPDNTREE)!
    const icd10Tree = useAppSelector((state) => state.settings.values.ICD10TREE)!
    const icd10IPDNTree = useAppSelector((state) => state.settings.values.ICD10IPDNTREE)!

    const formContents = () => {
        if (currentField) {
            const fieldId = currentField.field.code
            const fieldName = currentField.field.name
            const filter = _.find(userQuery.query.filters, { 'code': fieldId })
            const value: any = selected || (filter && filter.value ? filter.value.split(',') : undefined)

            const valuesWithLabels = () => {
                const labels = _.find(userQuery.query.filterLabels, { field: fieldId })!
                if (value) {
                    if (isCohortField(fieldId)) {
                        return value.map((code: string, index: number) => {
                            return {
                                label: cohortChoices ? cohortChoices.find(i => i.key === code)?.label : undefined,
                                value: code
                            }
                        })
                    } else {
                        return value.map((code: string, index: number) => {
                            return {label: labels.values[index], value: code}
                        })
                    }
                } else {
                    return undefined
                }
            }

            if (fieldId === 'diagpri') {
                if (okButton !== true) {
                    setOkButton(true)
                }
                return {
                    formValue: valuesWithLabels(),
                    formContent: <DiagnosisFilter
                        form={form}
                        fieldId={fieldId}
                        fieldName={fieldName}
                        operatorId={`${fieldId}oper`}
                        operatorChoices={[{label: 'Include', value: 'in'}, {label: 'Exclude', value: 'not in'}]}
                        operatorNotes={"Include or Exclude primary diagnoses codes from the results."}
                        treeData={icd10Tree}
                    />
                }
            } else if (fieldId === 'diagany') {
                if (okButton !== true) {
                    setOkButton(true)
                }
                return {
                    formValue: valuesWithLabels(),
                    formContent: <DiagnosisFilter
                        form={form}
                        fieldId={fieldId}
                        fieldName={fieldName}
                        operatorId={`${fieldId}oper`}
                        operatorChoices={[{label: 'Include', value: 'in'}, {label: 'Exclude', value: 'not in'}]}
                        operatorNotes={"Include or Exclude diagnoses codes from results regardless of diagnosis position."}
                        treeData={icd10Tree}
                    />
                }
            } else if (fieldId === 'diagsec') {
                if (okButton !== true) {
                    setOkButton(true)
                }
                return {
                    formValue: valuesWithLabels(),
                    formContent: <DiagnosisFilter
                        form={form}
                        fieldId={fieldId}
                        fieldName={fieldName}
                        operatorId={`${fieldId}oper`}
                        operatorChoices={[{ label: 'AND', value: 'and' }, { label: 'OR', value: 'or' }]}
                        operatorNotes={"Include diagnoses codes in addition to, or instead of, other specified 'Diagnosis' filters. If used with no other specified 'Diagnosis' filters, the 'AND / OR' options are negated and results include ALL selected codes."}
                        treeData={icd10Tree}
                    />
                }
            } else if (fieldId === 'diagsecnot') {
                if (okButton !== true) {
                    setOkButton(true)
                }
                return {
                    formValue: valuesWithLabels(),
                    formContent: <DiagnosisFilter
                        form={form}
                        fieldId={fieldId}
                        fieldName={fieldName}
                        operatorId={`${fieldId}oper`}
                        operatorChoices={[{ label: 'AND', value: 'and' }, { label: 'NOT', value: 'not in' }]}
                        operatorNotes={"Include diagnoses codes ('AND' option) or exclude diagnoses codes ('NOT' option) in addition to other specified 'Diagnosis' filters."}
                        treeData={icd10Tree}
                    />
                }
            } else if (fieldId === 'diagipdnhbwales') {
                if (okButton !== true) {
                    setOkButton(true)
                }
                return {
                    formValue: valuesWithLabels(),
                    formContent: <DiagnosisFilter
                        form={form}
                        fieldId={fieldId}
                        fieldName={fieldName}
                        treeData={icd10IPDNTree}
                        useIpdn={true}
                    />
                }
            } else if (currentField.field.dialogType === 'like') {
                if (okButton !== true) {
                    setOkButton(true)
                }
                if (allButtonStatus !== 'hide') {
                    setAllButtonStatus('hide')
                }
                return {
                    formValue: value,
                    formContent: <LikeFilter
                        form={form}
                        fieldId={fieldId}
                        fieldName={fieldName}
                    />
                }
            } else if (currentField.field.dialogType === 'proc') {
                if (okButton !== true) {
                    setOkButton(true)
                }
                return {
                    formValue: valuesWithLabels(),
                    formContent: <ProceduresSelector
                        form={form}
                        fieldId={fieldId}
                        fieldName={''}
                        procedureTree={procedureTree}
                    />
                }
            } else if (fieldId === 'procipdnhbwales') {
                if (okButton !== true) {
                    setOkButton(true)
                }
                return {
                    formValue: valuesWithLabels(),
                    formContent: <ProceduresSelector
                        form={form}
                        fieldId={fieldId}
                        fieldName={''}
                        procedureTree={procedureIPDNTree}
                        useIpdn={true}
                    />
                }
            } else if (currentField.field.dialogType === 'period') {
                if (okButton !== true) {
                    setOkButton(true)
                }
                return {
                    formValue: value,
                    formContent: <PeriodFilter
                        form={form}
                        fieldId={fieldId}
                        fieldName={fieldName}
                    />
                }
            } else if (currentField.field.dialogType === 'waittime') {
                if (okButton !== true) {
                    setOkButton(true)
                }
                return {
                    formValue: value,
                    formContent: <WaitingTimeFilter
                        form={form}
                        fieldId={fieldId}
                        fieldName={fieldName}
                    />
                }
            } else if (currentField.field.dialogType === 'beddayscustom') {
                if (okButton !== true) {
                    setOkButton(true)
                }
                return {
                    formValue: value,
                    formContent: <MinMaxFilter
                        form={form}
                        fieldId={fieldId}
                        minLabel={"Minimum Bed Day(s)"}
                        maxLabel={"Maximum Bed Day(s)"}
                    />
                }
            } else if (currentField.field.filterType === ('generictree')) {
                if (okButton !== true) {
                    setOkButton(true)
                }
                if (allButtonStatus !== 'hide') {
                    setAllButtonStatus('hide')
                }
                return {
                    formValue: valuesWithLabels(),
                    formContent: <GenericTreeFilter
                        form={form}
                        endpoint = {'field-choices'}
                        dialogType={currentField.field.norm}
                        fieldId={fieldId}
                        fieldName={fieldName}
                        showCheckedStrategy={'SHOW_CHILD'}
                    />
                }
            } else if (currentField.field.filterType === ('snomed')) {
                if (okButton !== true) {
                    setOkButton(true)
                }
                if (allButtonStatus !== 'hide') {
                    setAllButtonStatus('hide')
                }
                return {
                    formValue: valuesWithLabels(),
                    formContent: <GenericTreeFilter
                        form={form}
                        endpoint={'snomed-descriptions'}
                        dialogType={currentField.field.norm}
                        fieldId={fieldId}
                        fieldName={fieldName}
                        showCheckedStrategy={'SHOW_CHILD'}
                    />
                }
            } else if (currentField.field.filterType === ('ipdntree')) {
                if (okButton !== true) {
                    setOkButton(true)
                }
                if (allButtonStatus !== 'hide') {
                    setAllButtonStatus('hide')
                }
                return {
                    formValue: valuesWithLabels(),
                    formContent: <GenericTreeFilter
                        form={form}
                        endpoint={'ipdntree'}
                        dialogType={currentField.field.norm}
                        fieldId={fieldId}
                        fieldName={fieldName}
                        showCheckedStrategy={'SHOW_CHILD'}
                    />
                }
            } else if (currentField.field.dialogType === 'agecustom') {
                if (okButton !== true) {
                    setOkButton(true)
                }
                const text = <>
                    For patients under 1 year, please use 0 as the age value. <br />
                    If no minimum or maximum value is entered, the minimum or maximum available age will be returned.
                </>
                return {
                    formValue: value,
                    formContent: <MinMaxFilter
                        form={form}
                        fieldId={fieldId}
                        text={text}
                        minValue={MIN_AGE}
                        minLabel={"Minimum Age"}
                        maxValue={MAX_AGE}
                        maxLabel={"Maximum Age"}
                    />
                }
            } else if (currentField.field.dialogType === 'select_remote') {
                if (okButton !== true) {
                    setOkButton(true)
                }
                if (allButtonStatus !== 'hide') {
                    setAllButtonStatus('hide')
                }
                return {
                    formValue: valuesWithLabels(),
                    formContent: <RemoteSelect
                        form={form}
                        fieldId={fieldId}
                        placeholder={`Search ${fieldName}`}
                        value={value}
                        // fetchOptions={fetchOptionsList}
                        fetchOptions={fetchOptionsList(currentField.field.norm)}
                        // onChange={(newValue) => {
                        //     setValue(newValue as LabelAndValue[])
                        // }}
                        style={{width: '100%'}}
                    />
                }
            } else if (currentField.field.dialogType === 'snomed_ct') {
                if (okButton !== true) {
                    setOkButton(true)
                }
                if (allButtonStatus !== 'hide') {
                    setAllButtonStatus('hide')
                }
                return {
                    formValue: valuesWithLabels(),
                    formContent: <ClinicalEventFilter
                        form={form}
                        fieldId={fieldId}
                    />,
                    modalStyle: { width: "800px"}
                }
            } else if (currentField.field.dialogType === 'cohort_patients') {
                if (okButton !== true) {
                    setOkButton(true)
                }
                return {
                    formValue: valuesWithLabels(),
                    formContent: <CohortsFilter
                        form={form}
                        fieldId={fieldId}
                        cohortId={cohortId}
                    />,
                    modalStyle: { width: "900px"}
                }
            } else if ((currentField.field.code === 'clinicalevent_custom_opcrdc'|| currentField.field.code === 'treatment_custom_opcrdt') && choices) {

                if (allButtonStatus !== 'hide') {
                    setAllButtonStatus('hide')
                }

                const codeChoices = value && choices.filter(choice => {
                    const choiceValues = choice.code.split(",");
                    const valuesSelected = value.join(",");
                    return choiceValues.every(choiceValue => valuesSelected.includes(choiceValue));
                })

                return {
                    formValue: codeChoices?.map(codeChoice => codeChoice.code),
                    formContent: <>
                    <Form.Item name={`${fieldId}_labels`} hidden={true}>
                        <Input />
                    </Form.Item>
                    <Form.Item name={fieldId}>
                        <SelectWrapper
                            mode="multiple"
                            style={{ width: '100%' }}
                            placeholder={`Select ${currentField.field.name}`}
                            onChange={handleSelectChange}
                            filterOption={(input, option) => {
                                return option ? option.title.toLowerCase().indexOf(input.toLowerCase()) >= 0 : false
                            }}
                            value={value}
                        >
                            {choices
                                .map(i => {
                                    const title = i.opt;
                                    return <Option key={i.code} title={title} value={i.code}>{title}</Option>
                                })}
                        </SelectWrapper>
                    </Form.Item>
                </>
                }
            } else if (choices) {
                if (currentField!.field.dialogType === 'radiobox') {
                    if (okButton !== true) {
                        setOkButton(true)
                    }
                    if (allButtonStatus !== 'hide') {
                        setAllButtonStatus('hide')
                    }
                    const handleCheckboxChange = (e: RadioChangeEvent) => {
                        const label = findLabel(choices, e.target.value)
                        form.setFieldValue(`${currentField!.field.code}_labels`, [label])
                    }
                    return {
                        formValue: value,
                        formContent: <>
                            <Form.Item name={`${fieldId}_labels`} hidden={true}>
                                <Input />
                            </Form.Item>
                            <Form.Item name={fieldId}>
                                <Radio.Group onChange={handleCheckboxChange}>
                                    <Space direction="vertical">
                                        {
                                            choices
                                                .map(i => <Radio key={i.code} value={i.code}>{i.opt}</Radio>)
                                        }
                                    </Space>
                                </Radio.Group>
                            </Form.Item>
                        </>
                    }
                } else if (choices.length >= 10 || currentField!.field.dialogType === 'dropdown') {
                    return {
                        formValue: value,
                        formContent: <>
                            <Form.Item name={`${fieldId}_labels`} hidden={true}>
                                <Input />
                            </Form.Item>
                            <Form.Item name={fieldId}>
                                <SelectWrapper
                                    mode="multiple"
                                    style={{ width: '100%' }}
                                    placeholder={`Select ${currentField.field.name}`}
                                    onChange={handleSelectChange}
                                    filterOption={(input, option) => {
                                        return option ? option.title.toLowerCase().indexOf(input.toLowerCase()) >= 0 : false
                                    }}
                                    value={value}
                                >
                                    {choices
                                        .map(i => {
                                            const code = `${currentField.field.presentation === 'label' ? '' : ' (' + i.code + ')'}`
                                            const title = `${i.opt}${code}`;
                                            return <Option key={i.code} title={title} value={i.code}>{title}</Option>
                                        })}
                                </SelectWrapper>
                            </Form.Item>
                        </>
                    }
                } else {
                    return {
                        formValue: value,
                        formContent: <>
                            <Form.Item name={`${fieldId}_labels`} hidden={true}>
                                <Input />
                            </Form.Item>
                            <Form.Item name={fieldId}>
                                <Checkbox.Group onChange={handleSelectChange}>
                                    <Row>
                                        {
                                            choices.map(i => {
                                                return <Col key={i.code} span={12}>
                                                    <Checkbox value={i.code} style={{ lineHeight: '32px' }}>{i.opt}</Checkbox>
                                                </Col>
                                            })
                                        }
                                    </Row>
                                </Checkbox.Group>
                            </Form.Item>
                        </>
                    }
                }
            }
        }
        return {
            formValue: undefined,
            formContent: <Spin />
        }
    }
    const { formValue, formContent, modalStyle } = formContents()

    useEffect(() => {
        if (currentField) {
            const fieldCode = currentField.field.code
            if (currentField.field.dialogType === 'radiobox' && _.isArray(formValue)) {
                form.setFieldValue(fieldCode, formValue[0])
            } else {
                form.setFieldsValue({[fieldCode]: formValue})
            }
            if (isCohortField(fieldCode)) {
                const PERIOD_FIELD = cohortPeriodField(fieldCode)
                const period = _.find(userQuery.query.filters, {'code': PERIOD_FIELD})
                form.setFieldValue(PERIOD_FIELD, period?.value)

                const MONTHS_FIELD = cohortMonthsField(fieldCode)
                const months = _.find(userQuery.query.filters, { 'code': MONTHS_FIELD })
                form.setFieldValue(MONTHS_FIELD, months?.value)

                if (cohortChoices) {
                    const description = cohortDescription(
                        fieldCode,
                        form.getFieldValue(PERIOD_FIELD),
                        form.getFieldValue(MONTHS_FIELD),
                        form.getFieldValue(fieldCode),
                        cohortChoices
                    )
                    form.setFieldValue(`${fieldCode}_labels`, description.values)
                }
            }
        }
    }, [currentField, formValue]) // eslint-disable-line react-hooks/exhaustive-deps

    return <FilterDialogContext.Provider value={{ okButton, setOkButton, allButtonStatus, setAllButtonStatus, onSelectAll, setOnSelectAll }}>
            <Modal
                title={`${currentField!.field.name}`}
                open={isFilterModalOpen}
                onCancel={handleCancel}
                width={modalStyle?.width}
                footer={[
                    <Button key="all" onClick={onSelectAll} disabled={allButtonStatus === 'disable'} hidden={allButtonStatus === 'hide'}>
                        Select All
                    </Button>,
                    <Button key="cancel" onClick={handleCancel}>
                        Cancel
                    </Button>,
                    <Button key="ok" type="primary" disabled={!okButton} onClick={form.submit} data-testid = 'filter-modal-ok-button'>
                        OK
                    </Button>,
                ]}
            >
                <Form
                    form={form}
                    name="basic"
                    style={{ width: '100%' }}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    autoComplete="off"
                    data-testid="filter-dialog-form"
                >
                    {formContent}
                </Form>
            </Modal>
        </FilterDialogContext.Provider>
}

export default FilterDialog